import { useCallback, useEffect, useRef, useState } from 'react';
import {
  base64PcmToFloat32,
  buildQwenRealtimeUrl,
  downsampleFloat32ToInt16Pcm,
  int16ArrayToBase64,
  qwenRealtimeVoiceEnabled,
  requestQwenRealtimeTicket,
  type QwenRealtimeUsage,
  type VoiceContextV1,
} from '../api/qwen-realtime';
import { isDefensiveNoImageAnswer } from './qwen-voice-display';
import { isAsrContextLeakTranscript } from './qwen-asr-leak-guard';
import {
  recordQwenVoiceTrace,
  summarizeQwenVoiceContext,
} from './qwen-voice-trace';
import {
  composeRotationSummary,
  lastExchangeForRotation,
  premintDelayMs,
  VOICE_ROTATION_RECONNECT_COPY_IT,
} from './voice-rotation';

type QwenVoiceStatus =
  | 'idle'
  | 'connecting'
  | 'listening'
  | 'thinking'
  | 'speaking'
  | 'blocked'
  | 'error';

export interface QwenAssistantDoneInfo {
  providerResponseId?: string;
  turnClientId?: string;
  providerUsage?: QwenRealtimeUsage;
  providerUsageHash?: string;
  turnProof?: string;
  cancelled: boolean;
}

export interface QwenLocalTranscriptResult {
  handledLocally: true;
  playback?: Promise<void>;
  context?: VoiceContextV1;
}

export interface QwenContextRefreshResult {
  handledLocally?: false;
  context: VoiceContextV1;
  responseGuard?: QwenResponseGuard;
}

export type QwenTranscriptResult = void | QwenLocalTranscriptResult | QwenContextRefreshResult;

export interface QwenResponseGuard {
  kind: 'visual-aid' | 'visible-images' | 'persona-boundary';
  context: VoiceContextV1;
  failureMessage?: string;
}

/** Voice unification §4 — the proxy's per-turn SERVER visual decision. The
 *  server is the single arbiter (its decision already framed the model's
 *  instructions before response.create was released); the FE mirrors it. */
export interface QwenServerVisualSetPayload {
  utteranceSeq: number;
  transactionId: string;
  action: 'replace' | 'keep' | 'clear_clarify';
  signs: Array<{ name: string; label: string }>;
  clarifyMessage?: string;
}

export interface UseQwenRealtimeVoiceOptions {
  sessionId?: string | null;
  context?: VoiceContextV1;
  onSession?: (sessionId: string) => void;
  onUserTranscriptFinal: (text: string) => QwenTranscriptResult;
  /** Apply the server's visual decision to the overlay. Return the render-ack
   *  context to push (a session.update follows), or void when nothing changed. */
  onServerVisualSet?: (payload: QwenServerVisualSetPayload) => VoiceContextV1 | void;
  onAssistantTranscriptDelta: (delta: string) => void;
  onAssistantTranscriptFinal: (text: string) => void;
  onAssistantDone: (info: QwenAssistantDoneInfo) => void;
  onAssistantGuardFailed?: (message: string) => void;
  onAssistantError: () => void;
  enabled: boolean;
}

export interface UseQwenRealtimeVoiceReturn {
  supported: boolean;
  active: boolean;
  listening: boolean;
  speaking: boolean;
  status: QwenVoiceStatus;
  /** True while a dropped call is silently re-establishing (policy renewal or a
   *  transient-drop retry). Lets the UI show a "Richiamando Il Prof…" label
   *  instead of a bare "connecting" during the reconnect gap (founder decision B). */
  reconnecting: boolean;
  error: string | null;
  start: () => Promise<void>;
  submitText: (text: string) => void;
  stop: () => void;
  interrupt: () => void;
}

interface QwenServerEvent {
  type?: string;
  delta?: string;
  transcript?: string;
  text?: string;
  response_id?: string;
  responseId?: string;
  providerResponseId?: string;
  providerUsage?: QwenRealtimeUsage;
  providerUsageHash?: string;
  response?: {
    id?: string;
    status?: string;
    usage?: QwenRealtimeUsage;
    status_details?: {
      type?: string;
      reason?: string;
    };
  };
  turnClientId?: string;
  turnProof?: string;
  error?: { message?: string };
}

const INPUT_CHUNK_MS = 20;
const VOICE_READY_TIMEOUT_MS = 10_000;
const QWEN_INPUT_WORKLET_NAME = 'qwen-realtime-input-capture';
const QWEN_INPUT_WORKLET_URL = new URL('./qwen-realtime-input.worklet.js', import.meta.url);
// Founder decision B (2026-06-03): raise the per-call policy-renewal ceiling from
// 4 to 8. The backend closes each provider socket at MAX_SOCKET_MS (2 min) with a
// policy_duration_limit; each renewal buys another ~2-min segment, so 8 renewals
// lets a single call run ~18 min instead of bumping the wall at ~10 min.
const MAX_POLICY_RECONNECTS_PER_CALL = 8;
// Founder decision C (2026-06-03): tolerate more transient network drops before
// giving up. Previously a single unexpected (non-policy) close was the whole
// budget, so two blips ended the call; allow up to 3 silent reconnects per call.
const MAX_NON_POLICY_RECONNECTS_PER_CALL = 3;
// After a barge-in cancel we must NOT fire the new response.create synchronously:
// the realtime server will not run two responses at once and silently drops the new
// turn (the call then sits idle — the perceived "freeze"). We defer the new response
// until the cancelled response's `response.done` arrives; this timeout is the safety
// net so the new turn still fires even if that `done` never comes.
const RESPONSE_AFTER_CANCEL_SETTLE_MS = 700;
const NON_ITALIAN_SCRIPT_RE = /[\u3040-\u30ff\u3400-\u9fff\uac00-\ud7af\u0400-\u04ff]/;
const ENGLISH_GHOST_WORDS = new Set([
  'a', 'about', 'agreed', 'am', 'and', 'are', 'be', 'but', 'he', 'i', 'in', 'is',
  'it', 'm', 'not', 'of', 'on', 'or', 's', 'she', 'simply', 'so', 'that', 'the',
  'they', 'this', 'to', 'we', 'worried', 'you', 'was', 'recording', 'note', 'for',
  'voice', 'changed', 'woman', 'man', 'before', 'while', 'speaking', 'couldn', 't',
  'conversation', 'stopped', 'right', 'supposed', 'behavior', 'tested', 'when', 'ask',
  'show', 'me',
]);
const ITALIAN_VOICE_MARKER_RE = /\b(?:allora|aspetta|autostrada|basta|capito|che|chilometri|cosa|devo|dimmi|esempio|falso|fammi|limite|limiti|non|perche|perchè|pioggia|puoi|ripeti|segnale|significa|spiega|spiegami|stop|velocita|velocità|vero)\b/;
const LOW_INFORMATION_ASR_WORDS = new Set([
  'ah',
  'boh',
  'cioe',
  'eh',
  'e',
  'io',
  'ma',
  'mah',
  'mm',
  'mmm',
  'ok',
  'okay',
  'quella',
  'quelle',
  'quelli',
  'quello',
  'questa',
  'queste',
  'questi',
  'questo',
  'si',
  'tipo',
  'quando',
]);

type WindowWithAudioConstructors = Window & {
  AudioContext?: typeof AudioContext;
  webkitAudioContext?: typeof AudioContext;
  AudioWorkletNode?: typeof AudioWorkletNode;
};

function getAudioContextCtor(): typeof AudioContext | null {
  if (typeof window === 'undefined') return null;
  const win = window as WindowWithAudioConstructors;
  return win.AudioContext ?? win.webkitAudioContext ?? null;
}

function getAudioWorkletNodeCtor(): typeof AudioWorkletNode | null {
  if (typeof window === 'undefined') return null;
  return (window as WindowWithAudioConstructors).AudioWorkletNode ?? null;
}

type QwenVoiceLifecyclePhase =
  | 'ticket_request_started'
  | 'ticket_issued'
  | 'socket_open'
  | 'session_updated'
  | 'media_setup_started'
  | 'listening'
  | 'injected_turn'
  | 'first_response'
  | 'completion'
  | 'cleanup';

function recordQwenVoiceLifecycle(
  phase: QwenVoiceLifecyclePhase,
  details?: Record<string, string | number | boolean | null>,
): void {
  recordQwenVoiceTrace({
    source: 'frontend-hook',
    type: 'status_changed',
    status: `lifecycle:${phase}`,
    ...(details ? { details } : {}),
  });
}

function browserSupportsRealtimeVoice(enabled: boolean): boolean {
  return (
    enabled &&
    qwenRealtimeVoiceEnabled() &&
    typeof window !== 'undefined' &&
    typeof WebSocket !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia &&
    !!getAudioContextCtor()
  );
}

function isCancelledResponse(data: QwenServerEvent): boolean {
  const status = data.response?.status?.toLowerCase();
  const statusType = data.response?.status_details?.type?.toLowerCase();
  const reason = data.response?.status_details?.reason?.toLowerCase();
  return status === 'cancelled' || statusType === 'cancelled' || reason === 'cancelled';
}

function normalizeSpokenControl(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function isLocalPauseOrStopControl(text: string): boolean {
  const normalized = normalizeSpokenControl(text);
  const politeTail = '(?: (?:un attimo|un secondo|per favore|cosi))*';
  return [
    new RegExp(`^(?:aspetta|attendi|ferma|ferma la voce|ferma voce|fermati|pausa|basta)${politeTail}$`),
    /^(?:un attimo|un secondo)$/,
    /^(?:dammi|mi dai) (?:un attimo|un secondo)(?: per favore)?$/,
    new RegExp(`^puoi (?:fermarti|fermare la voce|mettere in pausa)${politeTail}$`),
  ].some((pattern) => pattern.test(normalized));
}

function isLikelyEnglishGhostTranscript(normalized: string): boolean {
  if (normalized === 'stop') return false;
  if (ITALIAN_VOICE_MARKER_RE.test(normalized)) return false;
  if (/^(?:i m|i am|and it s|so he|so it|so they|it s|the|a|an)\b/.test(normalized)) return true;
  const words = normalized.split(' ').filter(Boolean);
  if (words.length < 2 || words.length > 18) return false;
  const englishCount = words.filter((word) => ENGLISH_GHOST_WORDS.has(word)).length;
  return englishCount >= Math.ceil(words.length * 0.6);
}

function isLowInformationAsrFragment(normalized: string): boolean {
  const words = normalized.split(' ').filter(Boolean);
  if (words.length === 0) return true;
  if (words.length > 3) return false;
  return words.every((word) => LOW_INFORMATION_ASR_WORDS.has(word));
}

function isDanglingAsrFragment(normalized: string): boolean {
  const words = normalized.split(' ').filter(Boolean);
  if (words.length === 0 || words.length > 5) return false;
  if (/\b(?:autostrada|bagnato|frenata|limite|limiti|pioggia|precedenza|rotatorie|segnale|segnali|stop|velocita|vero|falso)\b/.test(normalized)) {
    return false;
  }
  if (/^(?:ho|hai|avevo|avevi|ti ho|mi hai|gli ho|le ho)?\s*(?:chiesto|detto|domandato)\s+di$/.test(normalized)) {
    return true;
  }
  return /\b(?:a|che|con|di|e|per|se|su)$/.test(normalized);
}

function shouldIgnoreQwenUserTranscript(text: string): boolean {
  if (NON_ITALIAN_SCRIPT_RE.test(text)) return true;
  // ASR-leak guard: the Gummy biasing corpus can echo back as a giant in-domain
  // "user" transcript (founder live-mic bug). The fragment filters below let it
  // through (it is long + in-domain), so this dedicated guard drops it BEFORE it
  // becomes a turn / gets persisted. Checked on the raw text (it owns its own
  // normalization + length thresholds).
  if (isAsrContextLeakTranscript(text)) return true;
  const normalized = normalizeSpokenControl(text);
  if (!normalized) return true;
  return isLikelyEnglishGhostTranscript(normalized) ||
    isLowInformationAsrFragment(normalized) ||
    isDanglingAsrFragment(normalized);
}

function responseIdFromEvent(data: QwenServerEvent): string | null {
  return data.response?.id ?? data.response_id ?? data.responseId ?? null;
}

function isLocalTranscriptResult(value: QwenTranscriptResult): value is QwenLocalTranscriptResult {
  return Boolean(value && value.handledLocally);
}

function isContextRefreshResult(value: QwenTranscriptResult): value is QwenContextRefreshResult {
  return Boolean(value && !value.handledLocally && 'context' in value);
}

type GuardedTranscriptDecision = 'pending' | 'safe' | 'forbidden';

function isAppMetaOrHelpdeskAnswer(text: string): boolean {
  const normalized = normalizeSpokenControl(text);
  return /\b(?:app|applicazione|bug|debug|problema tecnico|qwen|sistema|websocket|ricaricare|refresh)\b/.test(normalized) ||
    /\b(?:chiamata|conversazione|microfono|voce)\b.{0,40}\b(?:bloccata|bloccato|cambiata|cambiato|chiusa|chiuso|errore|interrotta|interrotto|non funziona|problema)\b/.test(normalized);
}

function classifyGuardedTranscript(
  guard: QwenInternalResponseGuard | ActiveResponseGuard,
  text: string,
): GuardedTranscriptDecision {
  const clean = text.trim();
  if (!clean) return 'pending';
  if (isAppMetaOrHelpdeskAnswer(clean)) return 'forbidden';
  if ((guard.kind === 'visual-aid' || guard.kind === 'visible-images') && isDefensiveNoImageAnswer(clean)) {
    return 'forbidden';
  }
  if (clean.length >= 20 && /[.!?;:]\s*$/.test(clean)) return 'safe';
  return 'pending';
}

function defaultGuardFailureMessage(guard: QwenInternalResponseGuard | ActiveResponseGuard): string {
  if (guard.kind === 'persona-boundary') {
    return 'Riprendiamo il punto di prima: dimmi quale passaggio vuoi rivedere e lo colleghiamo subito a una regola Patente B.';
  }
  return 'Ho perso il contesto visivo di questo turno. Le immagini restano sullo schermo: chiedimi di nuovo cosa vuoi confrontare e riparto da quelle.';
}

function isExpectedActivePolicyClose(event: CloseEvent): boolean {
  return event.reason === 'policy_duration_limit' || (
    event.code === 1008 && /policy[_-]?duration[_-]?limit/i.test(event.reason)
  );
}

interface PendingResponseCreate {
  guard?: QwenInternalResponseGuard;
  userText?: string;
}

interface QwenInternalResponseGuard extends QwenResponseGuard {
  retryCount: number;
}

interface ActiveResponseGuard extends QwenInternalResponseGuard {
  responseId: string | null;
  transcript: string;
  audioQueue: string[];
  released: boolean;
}

function isVisualResponseGuard(guard: QwenInternalResponseGuard | ActiveResponseGuard | undefined): boolean {
  return guard?.kind === 'visual-aid' || guard?.kind === 'visible-images';
}

// Founder decision A (2026-06-03): conversation memory across the ~2-min socket
// renewal. The provider drops its in-RAM turn history on each rebuild, so we keep
// a short rolling client-side summary and re-send it in the reconnect context.
const MAX_SUMMARY_TURNS = 8;
const MAX_SUMMARY_CHARS = 1400;
const CONVERSATION_IMAGE_MARKER_RE = /\[\[image:[^\]]*\]\]/gi;

interface ConversationTurn {
  role: 'user' | 'prof';
  text: string;
}

/**
 * Build a short, deterministic summary of the last few turns to re-seed Il Prof
 * after a renewal. Spoken text only, image markers stripped, capped to the most
 * recent content — keeps continuity without any extra LLM call.
 */
function buildConversationSummary(turns: ConversationTurn[]): string | undefined {
  const recent = turns.slice(-MAX_SUMMARY_TURNS);
  const lines: string[] = [];
  for (const turn of recent) {
    const clean = turn.text.replace(CONVERSATION_IMAGE_MARKER_RE, '').replace(/\s+/g, ' ').trim();
    if (!clean) continue;
    lines.push(`${turn.role === 'user' ? 'Studente' : 'Prof'}: ${clean}`);
  }
  if (!lines.length) return undefined;
  let summary = lines.join('\n');
  if (summary.length > MAX_SUMMARY_CHARS) {
    // Keep the most recent content: trim from the front, then drop the partial line.
    summary = summary.slice(summary.length - MAX_SUMMARY_CHARS);
    const firstBreak = summary.indexOf('\n');
    if (firstBreak >= 0) summary = summary.slice(firstBreak + 1);
  }
  return summary || undefined;
}

export function useQwenRealtimeVoice(
  options: UseQwenRealtimeVoiceOptions,
): UseQwenRealtimeVoiceReturn {
  const [active, setActive] = useState(false);
  const [listening, setListening] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [status, setStatus] = useState<QwenVoiceStatus>('idle');
  const [reconnecting, setReconnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const supported = browserSupportsRealtimeVoice(options.enabled);

  const optionsRef = useRef(options);
  const wsRef = useRef<WebSocket | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioWorkletContextRef = useRef<AudioContext | null>(null);
  const processorRef = useRef<AudioWorkletNode | ScriptProcessorNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const captureSinkRef = useRef<GainNode | null>(null);
  const scheduledSourcesRef = useRef<AudioBufferSourceNode[]>([]);
  const nextPlaybackTimeRef = useRef(0);
  const activeRef = useRef(false);
  const listeningRef = useRef(false);
  const speakingRef = useRef(false);
  const epochRef = useRef(0);
  const pendingListenAfterPlaybackRef = useRef(false);
  const localPlaybackPendingRef = useRef(false);
  const suppressNextCancelledDoneRef = useRef(false);
  const responseInProgressRef = useRef(false);
  const firstResponseObservedRef = useRef(false);
  const activeResponseIdRef = useRef<string | null>(null);
  const discardedResponseIdsRef = useRef<Set<string>>(new Set());
  const discardUnidentifiedResponseRef = useRef(false);
  const ignoredInputResponseCountRef = useRef(0);
  const responseCreateRequestedRef = useRef(false);
  const pendingResponseCreateAfterSessionUpdateRef = useRef<PendingResponseCreate | null>(null);
  const pendingGuardForNextResponseRef = useRef<QwenInternalResponseGuard | null>(null);
  const activeResponseGuardRef = useRef<ActiveResponseGuard | null>(null);
  const lastAcceptedUserTranscriptRef = useRef<{ normalized: string } | null>(null);
  const nonPolicyReconnectCountRef = useRef(0);
  // Deferred response.create after a barge-in cancel (see RESPONSE_AFTER_CANCEL_SETTLE_MS).
  const pendingResponseAfterCancelRef = useRef<{ epoch: number; run: () => void } | null>(null);
  const pendingResponseAfterCancelTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const policyReconnectCountRef = useRef(0);
  const reconnectInProgressRef = useRef(false);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const readinessTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const conversationHistoryRef = useRef<ConversationTurn[]>([]);
  // FP7 §2 — in-window pre-mint (T-45s). The timer fires `premintDelayMs` after a
  // segment opens and pre-fetches the next ticket so reconnect has it in hand;
  // the pre-minted ticket is stashed here and consumed by the next start().
  const premintTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const premintedTicketRef = useRef<import('../api/qwen-realtime').QwenRealtimeTicketResponse | null>(null);
  const startRef = useRef<(() => Promise<void>) | null>(null);
  const latestTurnProofRef = useRef<{
    turnClientId: string;
    turnProof: string;
    providerResponseId?: string;
    providerUsage?: QwenRealtimeUsage;
    providerUsageHash?: string;
  } | null>(null);

  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  useEffect(() => {
    recordQwenVoiceTrace({
      source: 'frontend-hook',
      type: 'status_changed',
      status,
    });
  }, [status]);

  const clearReconnectTimer = useCallback(() => {
    if (!reconnectTimerRef.current) return;
    clearTimeout(reconnectTimerRef.current);
    reconnectTimerRef.current = null;
  }, []);

  const clearReadinessTimer = useCallback(() => {
    if (!readinessTimerRef.current) return;
    clearTimeout(readinessTimerRef.current);
    readinessTimerRef.current = null;
  }, []);

  const closeAudioContext = useCallback(() => {
    const context = audioContextRef.current;
    audioContextRef.current = null;
    audioWorkletContextRef.current = null;
    if (!context || context.state === 'closed') return;
    void context.close().catch(() => undefined);
  }, []);

  const isCurrentEpoch = useCallback((epoch: number) => (
    activeRef.current && epochRef.current === epoch
  ), []);

  const clearResponseTracking = useCallback(() => {
    responseInProgressRef.current = false;
    firstResponseObservedRef.current = false;
    activeResponseIdRef.current = null;
    discardedResponseIdsRef.current.clear();
    discardUnidentifiedResponseRef.current = false;
    ignoredInputResponseCountRef.current = 0;
    responseCreateRequestedRef.current = false;
    pendingResponseCreateAfterSessionUpdateRef.current = null;
    pendingGuardForNextResponseRef.current = null;
    activeResponseGuardRef.current = null;
    pendingResponseAfterCancelRef.current = null;
    if (pendingResponseAfterCancelTimerRef.current) {
      clearTimeout(pendingResponseAfterCancelTimerRef.current);
      pendingResponseAfterCancelTimerRef.current = null;
    }
  }, []);

  const recordConversationTurn = useCallback((role: 'user' | 'prof', text: string) => {
    const clean = text.replace(CONVERSATION_IMAGE_MARKER_RE, '').replace(/\s+/g, ' ').trim();
    if (!clean) return;
    const history = conversationHistoryRef.current;
    const last = history[history.length - 1];
    // De-dupe consecutive identical turns (guarded retries can re-emit the same text).
    if (last && last.role === role && last.text === clean) return;
    history.push({ role, text: clean });
    if (history.length > MAX_SUMMARY_TURNS * 3) {
      conversationHistoryRef.current = history.slice(-MAX_SUMMARY_TURNS * 2);
    }
  }, []);

  const withConversationMemory = useCallback((context: VoiceContextV1, isRotation = false): VoiceContextV1 => {
    const summary = buildConversationSummary(conversationHistoryRef.current);
    if (!isRotation) {
      // Within a single segment we only re-send the rolling summary (unchanged).
      return summary ? { ...context, conversationSummary: summary } : context;
    }
    // FP7 §2 rotation continuity: a resumed (rotated) segment also replays the
    // immediate last exchange (last student utterance + last Prof line) on top of
    // the rolling summary so Il Prof picks the thread up naturally. Both stay
    // within the existing server caps (1,500 summary / 1,200 item).
    const exchange = lastExchangeForRotation(conversationHistoryRef.current);
    const composed = composeRotationSummary(summary, exchange);
    return composed ? { ...context, conversationSummary: composed } : context;
  }, []);

  // FP7 §2 — clear any pending pre-mint timer + stashed ticket (call boundaries).
  const clearPremint = useCallback(() => {
    if (premintTimerRef.current) {
      clearTimeout(premintTimerRef.current);
      premintTimerRef.current = null;
    }
    premintedTicketRef.current = null;
  }, []);

  // FP7 §2 — schedule the in-window pre-mint at T-45s of the 2-min wall. Best-
  // effort: a failed pre-mint just leaves the lazy reconnect mint as the
  // fallback (start() mints fresh when no pre-minted ticket is stashed).
  const schedulePremint = useCallback((epoch: number) => {
    if (premintTimerRef.current) clearTimeout(premintTimerRef.current);
    premintTimerRef.current = setTimeout(() => {
      premintTimerRef.current = null;
      if (!activeRef.current || epochRef.current !== epoch) return;
      void (async () => {
        try {
          const issued = await requestQwenRealtimeTicket({
            sessionId: optionsRef.current.sessionId ?? undefined,
            context: optionsRef.current.context
              ? withConversationMemory(optionsRef.current.context, true)
              : undefined,
          });
          // Only keep it if the same segment is still live (avoid a stale ticket).
          if (activeRef.current && epochRef.current === epoch) {
            premintedTicketRef.current = issued;
            recordQwenVoiceTrace({ source: 'frontend-hook', type: 'context_update_queued', details: { phase: 'rotation_premint_ready' } });
          }
        } catch {
          // Lazy reconnect mint remains the fallback — no-op.
        }
      })();
    }, premintDelayMs());
  }, [withConversationMemory]);

  // FP7 §2/§4 — speak the rotation reconnect copy at most once per rotation.
  const speakReconnectCopyOnce = useCallback(() => {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;
    try {
      const u = new SpeechSynthesisUtterance(VOICE_ROTATION_RECONNECT_COPY_IT);
      u.lang = 'it-IT';
      window.speechSynthesis.speak(u);
    } catch {
      // Reconnect copy is a nicety — never throw into the rotation path.
    }
  }, []);

  const markActiveResponseDiscarded = useCallback(() => {
    const responseId = activeResponseIdRef.current;
    if (responseId) {
      discardedResponseIdsRef.current.add(responseId);
    } else if (responseInProgressRef.current) {
      discardUnidentifiedResponseRef.current = true;
    }
  }, []);

  const isDiscardedResponseEvent = useCallback((data: QwenServerEvent): boolean => {
    const responseId = responseIdFromEvent(data);
    if (responseId) return discardedResponseIdsRef.current.has(responseId);
    return discardUnidentifiedResponseRef.current;
  }, []);

  const hasUnresolvedAssistantTransaction = useCallback((): boolean => (
    responseInProgressRef.current ||
    responseCreateRequestedRef.current ||
    pendingResponseCreateAfterSessionUpdateRef.current !== null ||
    pendingResponseAfterCancelRef.current !== null ||
    pendingGuardForNextResponseRef.current !== null ||
    activeResponseGuardRef.current !== null ||
    speakingRef.current ||
    scheduledSourcesRef.current.length > 0
  ), []);

  const send = useCallback((payload: Record<string, unknown>, epoch = epochRef.current) => {
    if (!isCurrentEpoch(epoch)) return;
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    const messageType = typeof payload.type === 'string' ? payload.type : undefined;
    if (messageType !== 'input_audio_buffer.append') {
      recordQwenVoiceTrace({
        source: 'frontend-hook',
        type: 'client_event_sent',
        contextSummary: summarizeQwenVoiceContext(payload.context as VoiceContextV1 | undefined),
        details: {
          messageType: messageType ?? 'unknown',
          hasContext: Boolean(payload.context),
        },
      });
    }
    ws.send(JSON.stringify({
      event_id: `event_${Date.now()}_${Math.random().toString(16).slice(2)}`,
      ...payload,
    }));
  }, [isCurrentEpoch]);

  const sendResponseCreate = useCallback((epoch: number, guard?: QwenInternalResponseGuard) => {
    if (!isCurrentEpoch(epoch)) return;
    firstResponseObservedRef.current = false;
    responseCreateRequestedRef.current = true;
    pendingGuardForNextResponseRef.current = guard ?? null;
    send({ type: 'response.create' }, epoch);
  }, [isCurrentEpoch, send]);

  const recordFirstResponse = useCallback((epoch: number, responseId?: string | null) => {
    if (!isCurrentEpoch(epoch) || firstResponseObservedRef.current) return;
    firstResponseObservedRef.current = true;
    recordQwenVoiceLifecycle('first_response', {
      identified: Boolean(responseId),
    });
  }, [isCurrentEpoch]);

  const clearPendingResponseAfterCancel = useCallback(() => {
    pendingResponseAfterCancelRef.current = null;
    if (pendingResponseAfterCancelTimerRef.current) {
      clearTimeout(pendingResponseAfterCancelTimerRef.current);
      pendingResponseAfterCancelTimerRef.current = null;
    }
  }, []);

  // Fire the response.create that was deferred after a barge-in cancel, once the
  // cancelled response has settled (its `response.done`) or the safety timeout fires.
  const flushPendingResponseAfterCancel = useCallback((epoch: number) => {
    const pending = pendingResponseAfterCancelRef.current;
    if (!pending) return;
    clearPendingResponseAfterCancel();
    if (pending.epoch !== epoch || !isCurrentEpoch(epoch)) return;
    pending.run();
  }, [clearPendingResponseAfterCancel, isCurrentEpoch]);

  const sendUserTextItem = useCallback((epoch: number, text: string) => {
    if (!isCurrentEpoch(epoch)) return;
    const clean = text.replace(/\s+/g, ' ').trim();
    if (!clean) return;
    send({
      type: 'conversation.item.create',
      item: {
        type: 'message',
        role: 'user',
        content: [{ type: 'input_text', text: clean }],
      },
    }, epoch);
  }, [isCurrentEpoch, send]);

  const queueResponseCreateAfterSessionUpdate = useCallback((
    epoch: number,
    context: VoiceContextV1,
    guard?: QwenInternalResponseGuard,
    userText?: string,
  ) => {
    if (!isCurrentEpoch(epoch)) return;
    pendingResponseCreateAfterSessionUpdateRef.current = {
      ...(guard ? { guard } : {}),
      ...(userText ? { userText } : {}),
    };
    recordQwenVoiceTrace({
      source: 'frontend-hook',
      type: 'context_update_queued',
      contextSummary: summarizeQwenVoiceContext(context),
      details: {
        guarded: Boolean(guard),
      },
    });
    send({ type: 'session.update', context: withConversationMemory(context) }, epoch);
  }, [isCurrentEpoch, send, withConversationMemory]);

  const stopPlayback = useCallback(() => {
    for (const source of scheduledSourcesRef.current) {
      try { source.stop(); } catch { /* noop */ }
    }
    scheduledSourcesRef.current = [];
    nextPlaybackTimeRef.current = 0;
    speakingRef.current = false;
    setSpeaking(false);
  }, []);

  const cancelUnresolvedAssistantForNewUserIntent = useCallback((epoch: number, text: string): boolean => {
    if (!isCurrentEpoch(epoch) || !hasUnresolvedAssistantTransaction()) return false;
    const requiresProviderSettlement =
      responseInProgressRef.current ||
      responseCreateRequestedRef.current ||
      speakingRef.current ||
      scheduledSourcesRef.current.length > 0;
    const guard =
      activeResponseGuardRef.current ??
      pendingGuardForNextResponseRef.current ??
      pendingResponseCreateAfterSessionUpdateRef.current?.guard;
    const interruptedText = activeResponseGuardRef.current?.transcript.trim() ||
      'Interrompo qui e passo alla tua nuova domanda.';
    recordQwenVoiceTrace({
      source: 'frontend-hook',
      type: 'active_transaction_interrupted',
      responseId: activeResponseIdRef.current ?? undefined,
      textSnippet: text,
      contextSummary: summarizeQwenVoiceContext(guard?.context),
      details: {
        guardKind: guard?.kind,
        hadActiveResponse: responseInProgressRef.current,
        hadRequestedResponse: responseCreateRequestedRef.current,
        hadPendingResponseCreate: pendingResponseCreateAfterSessionUpdateRef.current !== null,
        released: activeResponseGuardRef.current?.released ?? false,
      },
    });
    if (isVisualResponseGuard(guard)) {
      optionsRef.current.onAssistantTranscriptFinal(interruptedText);
    }
    markActiveResponseDiscarded();
    pendingListenAfterPlaybackRef.current = false;
    suppressNextCancelledDoneRef.current = true;
    pendingResponseCreateAfterSessionUpdateRef.current = null;
    pendingGuardForNextResponseRef.current = null;
    activeResponseGuardRef.current = null;
    responseInProgressRef.current = false;
    activeResponseIdRef.current = null;
    responseCreateRequestedRef.current = false;
    latestTurnProofRef.current = null;
    stopPlayback();
    send({ type: 'response.cancel' }, epoch);
    return requiresProviderSettlement;
  }, [
    hasUnresolvedAssistantTransaction,
    isCurrentEpoch,
    markActiveResponseDiscarded,
    send,
    stopPlayback,
  ]);

  const stopCapture = useCallback(() => {
    const processor = processorRef.current;
    if (processor && 'onaudioprocess' in processor) processor.onaudioprocess = null;
    if (processor && 'port' in processor) {
      processor.port.onmessage = null;
      processor.port.close();
    }
    processor?.disconnect();
    sourceRef.current?.disconnect();
    captureSinkRef.current?.disconnect();
    processorRef.current = null;
    sourceRef.current = null;
    captureSinkRef.current = null;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    listeningRef.current = false;
    setListening(false);
  }, []);

  const closeSocket = useCallback((code: number, reason: string) => {
    const ws = wsRef.current;
    wsRef.current = null;
    try { ws?.close(code, reason); } catch { /* noop */ }
  }, []);

  const failSession = useCallback((epoch: number, message: string, blocked = false) => {
    if (epochRef.current !== epoch) return;
    epochRef.current += 1;
    activeRef.current = false;
    pendingListenAfterPlaybackRef.current = false;
    localPlaybackPendingRef.current = false;
    suppressNextCancelledDoneRef.current = false;
    nonPolicyReconnectCountRef.current = 0;
    policyReconnectCountRef.current = 0;
    reconnectInProgressRef.current = false;
    clearReconnectTimer();
    clearReadinessTimer();
    clearPremint();
    clearResponseTracking();
    latestTurnProofRef.current = null;
    stopCapture();
    stopPlayback();
    closeAudioContext();
    closeSocket(1011, blocked ? 'microphone_unavailable' : 'voice_error');
    recordQwenVoiceLifecycle('cleanup', { reason: blocked ? 'microphone_unavailable' : 'voice_error' });
    setActive(false);
    setReconnecting(false);
    setError(message);
    setStatus(blocked ? 'blocked' : 'error');
    optionsRef.current.onAssistantError();
  }, [
    clearPremint,
    clearReadinessTimer,
    clearReconnectTimer,
    clearResponseTracking,
    closeAudioContext,
    closeSocket,
    stopCapture,
    stopPlayback,
  ]);

  // First-utterance warm-up note (founder report: "problems understanding the
  // first few messages"): capture is started AFTER `session.updated` (correct — we
  // must not stream audio before the session is configured), then getUserMedia
  // attaches with echoCancellation/noiseSuppression/autoGainControl. Those WebRTC
  // processors plus the Gummy recognizer both CONVERGE over the first ~0.5-1s of
  // audio, so the very first utterance can be degraded. We do NOT discard or delay
  // early audio (no mechanical bug here — discarding would lose the student's first
  // words); this is provider/browser warm-up behavior, documented for future tuning
  // (a short pre-roll tone or a "parla pure" prompt could mask it if it persists).
  const startCapture = useCallback(async (epoch: number) => {
    const AudioCtor = getAudioContextCtor();
    if (!AudioCtor || !isCurrentEpoch(epoch)) return;
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    if (streamRef.current && sourceRef.current && processorRef.current) {
      listeningRef.current = true;
      setListening(true);
      setReconnecting(false);
      clearReadinessTimer();
      recordQwenVoiceLifecycle('listening', { capturePath: 'existing' });
      if (!speakingRef.current) setStatus('listening');
      return;
    }
    try {
      stopCapture();
      recordQwenVoiceLifecycle('media_setup_started');
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: { ideal: true },
          noiseSuppression: { ideal: true },
          autoGainControl: { ideal: true },
          channelCount: { ideal: 1 },
        },
      });
      if (!isCurrentEpoch(epoch)) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      streamRef.current = stream;
      const ctx = audioContextRef.current ?? new AudioCtor();
      audioContextRef.current = ctx;
      if (ctx.state === 'suspended') await ctx.resume();
      if (!isCurrentEpoch(epoch)) {
        stopCapture();
        return;
      }
      const source = ctx.createMediaStreamSource(stream);
      const captureSink = ctx.createGain();
      captureSink.gain.value = 0;
      const createScriptProcessorFallback = (): ScriptProcessorNode => {
        const scriptProcessor = ctx.createScriptProcessor(2048, 1, 1);
        let lastSent = 0;
        scriptProcessor.onaudioprocess = (event) => {
          if (!isCurrentEpoch(epoch)) return;
          const now = performance.now();
          if (now - lastSent < INPUT_CHUNK_MS) return;
          lastSent = now;
          const input = event.inputBuffer.getChannelData(0);
          const pcm = downsampleFloat32ToInt16Pcm(input, ctx.sampleRate, 16000);
          send({ type: 'input_audio_buffer.append', audio: int16ArrayToBase64(pcm) }, epoch);
        };
        return scriptProcessor;
      };

      let processor: AudioWorkletNode | ScriptProcessorNode;
      let capturePath: 'audio_worklet' | 'script_processor_fallback';
      const AudioWorkletCtor = getAudioWorkletNodeCtor();
      if (ctx.audioWorklet && AudioWorkletCtor) {
        try {
          if (audioWorkletContextRef.current !== ctx) {
            await ctx.audioWorklet.addModule(QWEN_INPUT_WORKLET_URL);
            if (!isCurrentEpoch(epoch)) {
              stopCapture();
              return;
            }
            audioWorkletContextRef.current = ctx;
          }
          const worklet = new AudioWorkletCtor(ctx, QWEN_INPUT_WORKLET_NAME, {
            numberOfInputs: 1,
            numberOfOutputs: 1,
            outputChannelCount: [1],
          });
          worklet.port.onmessage = (event: MessageEvent<Float32Array>) => {
            if (!isCurrentEpoch(epoch)) return;
            const input = event.data;
            if (!(input instanceof Float32Array)) return;
            const pcm = downsampleFloat32ToInt16Pcm(input, ctx.sampleRate, 16000);
            send({ type: 'input_audio_buffer.append', audio: int16ArrayToBase64(pcm) }, epoch);
          };
          processor = worklet;
          capturePath = 'audio_worklet';
        } catch {
          if (!isCurrentEpoch(epoch)) {
            stopCapture();
            return;
          }
          processor = createScriptProcessorFallback();
          capturePath = 'script_processor_fallback';
        }
      } else {
        processor = createScriptProcessorFallback();
        capturePath = 'script_processor_fallback';
      }

      sourceRef.current = source;
      processorRef.current = processor;
      captureSinkRef.current = captureSink;
      source.connect(processor);
      processor.connect(captureSink);
      captureSink.connect(ctx.destination);
      listeningRef.current = true;
      setListening(true);
      setReconnecting(false);
      setStatus('listening');
      clearReadinessTimer();
      recordQwenVoiceLifecycle('listening', { capturePath });
    } catch {
      failSession(epoch, 'Non riesco ad aprire il microfono. Controlla i permessi e riprova.', true);
    }
  }, [clearReadinessTimer, failSession, isCurrentEpoch, send, stopCapture]);

  const resumeAfterLocalPlayback = useCallback(async (epoch: number, playback?: Promise<void>) => {
    try {
      await (playback ?? Promise.resolve());
    } catch {
      // Local browser TTS failures should not leave the live call stuck.
    }
    if (!isCurrentEpoch(epoch)) return;
    localPlaybackPendingRef.current = false;
    setStatus('listening');
    if (!listeningRef.current) void startCapture(epoch);
  }, [isCurrentEpoch, startCapture]);

  const playPcmChunk = useCallback((base64: string, epoch: number) => {
    if (!isCurrentEpoch(epoch)) return;
    const AudioCtor = getAudioContextCtor();
    if (!AudioCtor) return;
    const ctx = audioContextRef.current ?? new AudioCtor();
    audioContextRef.current = ctx;
    if (ctx.state === 'suspended') void ctx.resume().catch(() => undefined);
    const samples = base64PcmToFloat32(base64);
    const buffer = ctx.createBuffer(1, samples.length, 24000);
    buffer.copyToChannel(samples, 0);
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);
    const startAt = Math.max(ctx.currentTime + 0.02, nextPlaybackTimeRef.current || 0);
    source.start(startAt);
    nextPlaybackTimeRef.current = startAt + buffer.duration;
    scheduledSourcesRef.current.push(source);
    source.onended = () => {
      if (!isCurrentEpoch(epoch)) return;
      scheduledSourcesRef.current = scheduledSourcesRef.current.filter((s) => s !== source);
      if (scheduledSourcesRef.current.length === 0) {
        speakingRef.current = false;
        setSpeaking(false);
        if (pendingListenAfterPlaybackRef.current) {
          pendingListenAfterPlaybackRef.current = false;
          void startCapture(epoch);
        } else if (streamRef.current) {
          setStatus('listening');
        }
      }
    };
    speakingRef.current = true;
    setSpeaking(true);
  }, [isCurrentEpoch, startCapture]);

  const stop = useCallback(() => {
    epochRef.current += 1;
    activeRef.current = false;
    pendingListenAfterPlaybackRef.current = false;
    localPlaybackPendingRef.current = false;
    suppressNextCancelledDoneRef.current = false;
    nonPolicyReconnectCountRef.current = 0;
    policyReconnectCountRef.current = 0;
    reconnectInProgressRef.current = false;
    clearReconnectTimer();
    clearReadinessTimer();
    clearResponseTracking();
    latestTurnProofRef.current = null;
    conversationHistoryRef.current = [];
    // FP7 §2 — a user-initiated stop ends the call: drop the pre-mint timer AND
    // the stashed pre-minted ticket (no rotation will consume it).
    clearPremint();
    setActive(false);
    setStatus('idle');
    setReconnecting(false);
    setError(null);
    stopCapture();
    stopPlayback();
    closeAudioContext();
    closeSocket(1000, 'student_stopped');
    recordQwenVoiceLifecycle('cleanup', { reason: 'student_stopped' });
  }, [
    clearPremint,
    clearReadinessTimer,
    clearReconnectTimer,
    clearResponseTracking,
    closeAudioContext,
    closeSocket,
    stopCapture,
    stopPlayback,
  ]);

  const cancelAssistantResponse = useCallback((
    epoch: number,
    restartCapture: boolean,
    notifyDone = true,
  ): boolean => {
    if (!isCurrentEpoch(epoch)) return false;
    const hasAssistantWork =
      responseInProgressRef.current ||
      speakingRef.current ||
      scheduledSourcesRef.current.length > 0;
    if (!hasAssistantWork) return false;
    pendingListenAfterPlaybackRef.current = false;
    suppressNextCancelledDoneRef.current = true;
    markActiveResponseDiscarded();
    responseInProgressRef.current = false;
    activeResponseIdRef.current = null;
    activeResponseGuardRef.current = null;
    pendingGuardForNextResponseRef.current = null;
    responseCreateRequestedRef.current = false;
    latestTurnProofRef.current = null;
    stopPlayback();
    send({ type: 'response.cancel' }, epoch);
    if (notifyDone) optionsRef.current.onAssistantDone({ cancelled: true });
    if (restartCapture) {
      setStatus('listening');
      void startCapture(epoch);
    }
    return true;
  }, [isCurrentEpoch, markActiveResponseDiscarded, send, startCapture, stopPlayback]);

  const releaseGuardedResponse = useCallback((epoch: number) => {
    const guard = activeResponseGuardRef.current;
    if (!guard || guard.released || !isCurrentEpoch(epoch)) return;
    guard.released = true;
    recordQwenVoiceTrace({
      source: 'frontend-hook',
      type: 'audio_guard_released',
      responseId: guard.responseId ?? undefined,
      textSnippet: guard.transcript,
      contextSummary: summarizeQwenVoiceContext(guard.context),
      details: {
        kind: guard.kind,
        retryCount: guard.retryCount,
      },
    });
    if (guard.transcript) {
      optionsRef.current.onAssistantTranscriptDelta(guard.transcript);
    }
    const queuedAudio = [...guard.audioQueue];
    guard.audioQueue = [];
    setStatus('speaking');
    if (queuedAudio.length > 0 && !streamRef.current) void startCapture(epoch);
    for (const chunk of queuedAudio) {
      playPcmChunk(chunk, epoch);
    }
  }, [isCurrentEpoch, playPcmChunk, startCapture]);

  const failGuardedResponse = useCallback((epoch: number, retry: boolean) => {
    const guard = activeResponseGuardRef.current;
    if (!guard || !isCurrentEpoch(epoch)) return;
    recordQwenVoiceTrace({
      source: 'frontend-hook',
      type: 'audio_guard_failed',
      responseId: guard.responseId ?? undefined,
      textSnippet: guard.transcript,
      contextSummary: summarizeQwenVoiceContext(guard.context),
      details: {
        kind: guard.kind,
        retry,
        retryCount: guard.retryCount,
      },
    });
    markActiveResponseDiscarded();
    activeResponseGuardRef.current = null;
    pendingGuardForNextResponseRef.current = null;
    responseCreateRequestedRef.current = false;
    responseInProgressRef.current = false;
    activeResponseIdRef.current = null;
    latestTurnProofRef.current = null;
    suppressNextCancelledDoneRef.current = true;
    stopPlayback();
    send({ type: 'response.cancel' }, epoch);
    if (retry && guard.retryCount < 1) {
      const nextGuard: QwenInternalResponseGuard = {
        kind: guard.kind,
        context: guard.context,
        failureMessage: guard.failureMessage,
        retryCount: guard.retryCount + 1,
      };
      queueResponseCreateAfterSessionUpdate(epoch, guard.context, nextGuard);
      setStatus('thinking');
      return;
    }

    optionsRef.current.onAssistantGuardFailed?.(
      guard.failureMessage ??
        defaultGuardFailureMessage(guard),
    );
    setStatus('listening');
    if (!listeningRef.current) void startCapture(epoch);
  }, [isCurrentEpoch, markActiveResponseDiscarded, queueResponseCreateAfterSessionUpdate, send, startCapture, stopPlayback]);

  const failEmptyGuardedResponse = useCallback((epoch: number, guard: ActiveResponseGuard): boolean => {
    if (!isCurrentEpoch(epoch)) return false;
    recordQwenVoiceTrace({
      source: 'frontend-hook',
      type: 'audio_guard_failed',
      responseId: guard.responseId ?? undefined,
      contextSummary: summarizeQwenVoiceContext(guard.context),
      details: {
        kind: guard.kind,
        retry: guard.retryCount < 1,
        retryCount: guard.retryCount,
        reason: 'empty_transcript',
      },
    });
    activeResponseGuardRef.current = null;
    pendingGuardForNextResponseRef.current = null;
    responseCreateRequestedRef.current = false;
    responseInProgressRef.current = false;
    activeResponseIdRef.current = null;
    latestTurnProofRef.current = null;
    stopPlayback();

    if (guard.retryCount < 1) {
      const nextGuard: QwenInternalResponseGuard = {
        kind: guard.kind,
        context: guard.context,
        failureMessage: guard.failureMessage,
        retryCount: guard.retryCount + 1,
      };
      queueResponseCreateAfterSessionUpdate(epoch, guard.context, nextGuard);
      setStatus('thinking');
      return true;
    }

    optionsRef.current.onAssistantGuardFailed?.(
      guard.failureMessage ??
        defaultGuardFailureMessage(guard),
    );
    setStatus('listening');
    if (!listeningRef.current) void startCapture(epoch);
    return true;
  }, [isCurrentEpoch, queueResponseCreateAfterSessionUpdate, startCapture, stopPlayback]);

  const submitText = useCallback((text: string) => {
    const epoch = epochRef.current;
    if (!isCurrentEpoch(epoch)) return;
    const finalTranscript = text.trim();
    if (!finalTranscript) return;
    if (shouldIgnoreQwenUserTranscript(finalTranscript)) {
      recordQwenVoiceTrace({
        source: 'frontend-hook',
        type: 'user_transcript_ignored',
        textSnippet: finalTranscript,
        details: { reason: 'text_injection_asr_noise' },
      });
      return;
    }
    if (isLocalPauseOrStopControl(finalTranscript)) {
      recordQwenVoiceTrace({
        source: 'frontend-hook',
        type: 'user_transcript_ignored',
        textSnippet: finalTranscript,
        details: { reason: 'text_injection_local_pause_or_stop' },
      });
      return;
    }
    const normalizedTranscript = normalizeSpokenControl(finalTranscript);
    const isDuplicateWhileAnswerPending =
      lastAcceptedUserTranscriptRef.current?.normalized === normalizedTranscript &&
      hasUnresolvedAssistantTransaction();
    if (isDuplicateWhileAnswerPending) {
      recordQwenVoiceTrace({
        source: 'frontend-hook',
        type: 'user_transcript_ignored',
        textSnippet: finalTranscript,
        details: { reason: 'duplicate_text_injection' },
      });
      return;
    }
    const didCancelForNewIntent = cancelUnresolvedAssistantForNewUserIntent(epoch, finalTranscript);
    lastAcceptedUserTranscriptRef.current = { normalized: normalizedTranscript };
    recordQwenVoiceLifecycle('injected_turn');
    recordQwenVoiceTrace({
      source: 'frontend-hook',
      type: 'user_transcript_final',
      textSnippet: finalTranscript,
      details: { source: 'text_injection' },
    });
    recordConversationTurn('user', finalTranscript);
    const result = optionsRef.current.onUserTranscriptFinal(finalTranscript);
    if (isLocalTranscriptResult(result)) {
      cancelAssistantResponse(epoch, false, false);
      pendingListenAfterPlaybackRef.current = false;
      localPlaybackPendingRef.current = true;
      latestTurnProofRef.current = null;
      send({ type: 'input_audio_buffer.clear' }, epoch);
      if (result.context) send({ type: 'session.update', context: withConversationMemory(result.context) }, epoch);
      stopCapture();
      stopPlayback();
      setStatus('speaking');
      void resumeAfterLocalPlayback(epoch, result.playback);
      return;
    }
    const fireResponseCreate = () => {
      if (isContextRefreshResult(result)) {
        const guard = result.responseGuard
          ? {
              ...result.responseGuard,
              retryCount: 0,
            }
          : undefined;
        queueResponseCreateAfterSessionUpdate(epoch, result.context, guard, finalTranscript);
      } else {
        sendUserTextItem(epoch, finalTranscript);
        sendResponseCreate(epoch);
      }
    };
    if (didCancelForNewIntent) {
      clearPendingResponseAfterCancel();
      pendingResponseAfterCancelRef.current = { epoch, run: fireResponseCreate };
      pendingResponseAfterCancelTimerRef.current = setTimeout(() => {
        pendingResponseAfterCancelTimerRef.current = null;
        flushPendingResponseAfterCancel(epoch);
      }, RESPONSE_AFTER_CANCEL_SETTLE_MS);
    } else {
      fireResponseCreate();
    }
    setStatus('thinking');
    pendingListenAfterPlaybackRef.current = false;
    stopCapture();
  }, [
    cancelAssistantResponse,
    cancelUnresolvedAssistantForNewUserIntent,
    clearPendingResponseAfterCancel,
    flushPendingResponseAfterCancel,
    hasUnresolvedAssistantTransaction,
    isCurrentEpoch,
    queueResponseCreateAfterSessionUpdate,
    recordConversationTurn,
    resumeAfterLocalPlayback,
    send,
    sendResponseCreate,
    sendUserTextItem,
    stopCapture,
    stopPlayback,
    withConversationMemory,
  ]);

  const interrupt = useCallback(() => {
    const epoch = epochRef.current;
    if (!isCurrentEpoch(epoch)) return;
    const cancelled = cancelAssistantResponse(epoch, true);
    if (cancelled) return;
    pendingListenAfterPlaybackRef.current = false;
    suppressNextCancelledDoneRef.current = false;
    latestTurnProofRef.current = null;
    stopPlayback();
    send({ type: 'response.cancel' }, epoch);
    void startCapture(epoch);
  }, [cancelAssistantResponse, isCurrentEpoch, send, startCapture, stopPlayback]);

  const start = useCallback(async () => {
    if (!supported || activeRef.current) return;
    const isReconnect = reconnectInProgressRef.current;
    reconnectInProgressRef.current = false;
    clearReconnectTimer();
    if (!isReconnect) {
      nonPolicyReconnectCountRef.current = 0;
      policyReconnectCountRef.current = 0;
      // Fresh call (not a renewal) starts with a clean conversation memory.
      conversationHistoryRef.current = [];
    }
    const epoch = epochRef.current + 1;
    epochRef.current = epoch;
    activeRef.current = true;
    pendingListenAfterPlaybackRef.current = false;
    localPlaybackPendingRef.current = false;
    suppressNextCancelledDoneRef.current = false;
    clearResponseTracking();
    latestTurnProofRef.current = null;
    setActive(false);
    setListening(false);
    setSpeaking(false);
    setError(null);
    setStatus('connecting');
    clearReadinessTimer();
    readinessTimerRef.current = setTimeout(() => {
      readinessTimerRef.current = null;
      failSession(epoch, 'La voce AI non è pronta entro 10 secondi. Riprova.');
    }, VOICE_READY_TIMEOUT_MS);
    recordQwenVoiceLifecycle('ticket_request_started', { reconnect: isReconnect });

    let ws: WebSocket;
    try {
      // FP7 §2 — on a rotation reconnect, consume the in-window pre-minted ticket
      // (T-45s) if one is ready so the new segment opens without a fresh round-trip;
      // otherwise mint now (fresh call, or pre-mint missed). The reconnect context
      // carries the rolling summary + last exchange (rotation continuity).
      const preminted = isReconnect ? premintedTicketRef.current : null;
      premintedTicketRef.current = null;
      const issued = preminted ?? await requestQwenRealtimeTicket({
        sessionId: optionsRef.current.sessionId ?? undefined,
        context: optionsRef.current.context
          ? withConversationMemory(optionsRef.current.context, isReconnect)
          : undefined,
      });
      if (!isCurrentEpoch(epoch)) return;
      recordQwenVoiceLifecycle('ticket_issued', {
        reconnect: isReconnect,
        preminted: Boolean(preminted),
      });
      optionsRef.current.onSession?.(issued.sessionId);
      ws = new WebSocket(issued.wsUrl || buildQwenRealtimeUrl({ ticket: issued.ticket }));
      // FP7 §2/§4 — a resumed (rotated) segment speaks the reconnect copy once.
      if (isReconnect) speakReconnectCopyOnce();
    } catch {
      failSession(epoch, 'Non riesco ad aprire la voce AI. Riprova tra poco.');
      return;
    }
    if (!isCurrentEpoch(epoch)) {
      try { ws.close(1000, 'stale_voice_epoch'); } catch { /* noop */ }
      return;
    }

    wsRef.current = ws;
    setActive(true);
    let initialSessionUpdateAcked = false;
    let initialSessionUpdateResentAfterCreated = false;
    ws.onopen = () => {
      if (!isCurrentEpoch(epoch)) return;
      recordQwenVoiceLifecycle('socket_open', { reconnect: isReconnect });
      send({ type: 'session.update' }, epoch);
      // FP7 §2 — arm the in-window pre-mint for THIS segment's 2-min wall (T-45s).
      schedulePremint(epoch);
    };
    ws.onerror = () => {
      failSession(epoch, 'Non riesco ad aprire la voce AI. Riprova tra poco.');
    };
    ws.onclose = (event) => {
      if (epochRef.current !== epoch) return;
      if (wsRef.current === ws) wsRef.current = null;
      clearReadinessTimer();
      recordQwenVoiceTrace({
        source: 'frontend-hook',
        type: 'socket_closed',
        closeCode: event.code,
        closeReason: event.reason,
      });
      const policyClose = isExpectedActivePolicyClose(event);
      const shouldReconnect = activeRef.current && (
        policyClose
          ? policyReconnectCountRef.current < MAX_POLICY_RECONNECTS_PER_CALL
          : nonPolicyReconnectCountRef.current < MAX_NON_POLICY_RECONNECTS_PER_CALL
      );
      const closingGuard = activeResponseGuardRef.current;
      if (closingGuard && !closingGuard.released) {
        recordQwenVoiceTrace({
          source: 'frontend-hook',
          type: 'audio_guard_failed',
          responseId: closingGuard.responseId ?? undefined,
          textSnippet: closingGuard.transcript,
          closeCode: event.code,
          closeReason: event.reason,
          contextSummary: summarizeQwenVoiceContext(closingGuard.context),
          details: {
            kind: closingGuard.kind,
            retry: false,
            retryCount: closingGuard.retryCount,
            reason: 'socket_closed_before_safe_text',
          },
        });
        optionsRef.current.onAssistantGuardFailed?.(
          closingGuard.failureMessage ??
            defaultGuardFailureMessage(closingGuard),
        );
      }
      epochRef.current += 1;
      activeRef.current = false;
      pendingListenAfterPlaybackRef.current = false;
      localPlaybackPendingRef.current = false;
      suppressNextCancelledDoneRef.current = false;
      clearResponseTracking();
      latestTurnProofRef.current = null;
      // FP7 §2 — cancel this segment's pre-mint timer (the stashed pre-minted
      // ticket, if any, intentionally survives into the reconnecting start()).
      if (premintTimerRef.current) {
        clearTimeout(premintTimerRef.current);
        premintTimerRef.current = null;
      }
      stopCapture();
      stopPlayback();
      setActive(false);
      setListening(false);
      setSpeaking(false);
      if (shouldReconnect) {
        if (policyClose) {
          policyReconnectCountRef.current += 1;
        } else {
          nonPolicyReconnectCountRef.current += 1;
        }
        reconnectInProgressRef.current = true;
        setReconnecting(true);
        setError(null);
        setStatus('connecting');
        clearReconnectTimer();
        reconnectTimerRef.current = window.setTimeout(() => {
          reconnectTimerRef.current = null;
          void startRef.current?.();
        }, 0);
      } else {
        clearPremint();
        closeAudioContext();
        recordQwenVoiceLifecycle('cleanup', { reason: 'socket_closed' });
        setReconnecting(false);
        setStatus((current) => current === 'error' || current === 'blocked' ? current : 'idle');
      }
    };
    ws.onmessage = (event) => {
      if (!isCurrentEpoch(epoch) || typeof event.data !== 'string') return;
      let data: QwenServerEvent;
      try { data = JSON.parse(event.data) as QwenServerEvent; } catch { return; }
      const serverType = typeof data.type === 'string' ? data.type : 'unknown';
      if (serverType !== 'response.audio.delta') {
        recordQwenVoiceTrace({
          source: 'frontend-hook',
          type: 'server_event_received',
          responseId: responseIdFromEvent(data) ?? undefined,
          textSnippet: data.transcript ?? data.text ?? (serverType.includes('transcript') ? data.delta : undefined),
          details: { messageType: serverType },
        });
      }

      switch (data.type) {
        case 'session.created':
          if (!initialSessionUpdateAcked && !initialSessionUpdateResentAfterCreated) {
            initialSessionUpdateResentAfterCreated = true;
            send({ type: 'session.update' }, epoch);
          }
          break;
        case 'autoscuola.qwen_voice.turn_proof':
          if (data.turnClientId && data.turnProof) {
            latestTurnProofRef.current = {
              turnClientId: data.turnClientId,
              turnProof: data.turnProof,
              ...(data.providerResponseId ? { providerResponseId: data.providerResponseId } : {}),
              ...(data.providerUsage ? { providerUsage: data.providerUsage } : {}),
              ...(data.providerUsageHash ? { providerUsageHash: data.providerUsageHash } : {}),
            };
          }
          break;
        case 'autoscuola.qwen_voice.visual_set': {
          // Voice unification §4.3: the SERVER's per-turn visual decision. The
          // model was already grounded on it (the proxy's instructions update
          // preceded the released response.create); here the FE mirrors it on
          // screen and acks the render state so later turns carry the truth.
          const payload = data as unknown as Partial<QwenServerVisualSetPayload>;
          if (payload.action && Array.isArray(payload.signs) && typeof payload.transactionId === 'string') {
            recordQwenVoiceTrace({
              source: 'frontend-hook',
              type: 'server_event_received',
              textSnippet: payload.signs.map((s) => s?.name ?? '').join('; ').slice(0, 120),
              details: { messageType: 'visual_set', action: payload.action, transactionId: payload.transactionId },
            });
            const ackContext = optionsRef.current.onServerVisualSet?.(payload as QwenServerVisualSetPayload);
            if (ackContext) send({ type: 'session.update', context: withConversationMemory(ackContext) }, epoch);
          }
          break;
        }
        case 'session.updated':
          if (!initialSessionUpdateAcked) {
            recordQwenVoiceLifecycle('session_updated', { reconnect: isReconnect });
          }
          initialSessionUpdateAcked = true;
          if (pendingResponseCreateAfterSessionUpdateRef.current) {
            const pending = pendingResponseCreateAfterSessionUpdateRef.current;
            pendingResponseCreateAfterSessionUpdateRef.current = null;
            if (pending.userText) sendUserTextItem(epoch, pending.userText);
            sendResponseCreate(epoch, pending.guard);
            setStatus('thinking');
            break;
          }
          if (!localPlaybackPendingRef.current) void startCapture(epoch);
          break;
        case 'response.created': {
          const responseId = responseIdFromEvent(data);
          if (!responseCreateRequestedRef.current && ignoredInputResponseCountRef.current <= 0) {
            if (responseId) {
              discardedResponseIdsRef.current.add(responseId);
            } else {
              discardUnidentifiedResponseRef.current = true;
            }
            suppressNextCancelledDoneRef.current = true;
            send({ type: 'response.cancel' }, epoch);
            setStatus('listening');
            if (!listeningRef.current && !localPlaybackPendingRef.current) void startCapture(epoch);
            break;
          }
          responseCreateRequestedRef.current = false;
          activeResponseIdRef.current = responseId;
          responseInProgressRef.current = true;
          if (pendingGuardForNextResponseRef.current) {
            activeResponseGuardRef.current = {
              ...pendingGuardForNextResponseRef.current,
              responseId,
              transcript: '',
              audioQueue: [],
              released: false,
            };
            pendingGuardForNextResponseRef.current = null;
          } else {
            activeResponseGuardRef.current = null;
          }
          if (ignoredInputResponseCountRef.current > 0) {
            ignoredInputResponseCountRef.current -= 1;
            if (responseId) {
              discardedResponseIdsRef.current.add(responseId);
            } else {
              discardUnidentifiedResponseRef.current = true;
            }
            activeResponseGuardRef.current = null;
            suppressNextCancelledDoneRef.current = true;
            send({ type: 'response.cancel' }, epoch);
            if (localPlaybackPendingRef.current) {
              setStatus('speaking');
            } else {
              setStatus('listening');
              if (!listeningRef.current) void startCapture(epoch);
            }
          }
          break;
        }
        case 'response.output_item.added':
        case 'response.content_part.added':
          if (isDiscardedResponseEvent(data)) break;
          activeResponseIdRef.current = responseIdFromEvent(data) ?? activeResponseIdRef.current;
          responseInProgressRef.current = true;
          break;
        case 'input_audio_buffer.speech_started':
          cancelAssistantResponse(epoch, true);
          break;
        case 'conversation.item.input_audio_transcription.completed': {
          const finalTranscript = data.transcript?.trim();
          if (finalTranscript && shouldIgnoreQwenUserTranscript(finalTranscript)) {
            recordQwenVoiceTrace({
              source: 'frontend-hook',
              type: 'user_transcript_ignored',
              textSnippet: finalTranscript,
              details: { reason: 'asr_noise' },
            });
            cancelAssistantResponse(epoch, true);
            send({ type: 'input_audio_buffer.clear' }, epoch);
            setStatus('listening');
            if (!listeningRef.current) void startCapture(epoch);
            break;
          }
          if (finalTranscript && isLocalPauseOrStopControl(finalTranscript)) {
            recordQwenVoiceTrace({
              source: 'frontend-hook',
              type: 'user_transcript_ignored',
              textSnippet: finalTranscript,
              details: { reason: 'local_pause_or_stop' },
            });
            const cancelled = cancelAssistantResponse(epoch, true);
            if (!cancelled) {
              pendingListenAfterPlaybackRef.current = false;
              latestTurnProofRef.current = null;
              stopCapture();
              stopPlayback();
              send({ type: 'response.cancel' }, epoch);
              setStatus('listening');
              void startCapture(epoch);
            }
            break;
          }
          if (finalTranscript) {
            const normalizedTranscript = normalizeSpokenControl(finalTranscript);
            const isDuplicateWhileAnswerPending =
              lastAcceptedUserTranscriptRef.current?.normalized === normalizedTranscript &&
              hasUnresolvedAssistantTransaction();
            if (isDuplicateWhileAnswerPending) {
              recordQwenVoiceTrace({
                source: 'frontend-hook',
                type: 'user_transcript_ignored',
                textSnippet: finalTranscript,
                details: { reason: 'duplicate_final_transcript' },
              });
              send({ type: 'input_audio_buffer.clear' }, epoch);
              break;
            }
            const didCancelForNewIntent = cancelUnresolvedAssistantForNewUserIntent(epoch, finalTranscript);
            lastAcceptedUserTranscriptRef.current = { normalized: normalizedTranscript };
            recordQwenVoiceTrace({
              source: 'frontend-hook',
              type: 'user_transcript_final',
              textSnippet: finalTranscript,
            });
            recordConversationTurn('user', finalTranscript);
            const result = optionsRef.current.onUserTranscriptFinal(finalTranscript);
            if (isLocalTranscriptResult(result)) {
              cancelAssistantResponse(epoch, false, false);
              pendingListenAfterPlaybackRef.current = false;
              localPlaybackPendingRef.current = true;
              latestTurnProofRef.current = null;
              send({ type: 'input_audio_buffer.clear' }, epoch);
              if (result.context) send({ type: 'session.update', context: withConversationMemory(result.context) }, epoch);
              stopCapture();
              stopPlayback();
              setStatus('speaking');
              void resumeAfterLocalPlayback(epoch, result.playback);
              break;
            }
            const fireResponseCreate = () => {
              if (isContextRefreshResult(result)) {
                const guard = result.responseGuard
                  ? {
                      ...result.responseGuard,
                      retryCount: 0,
                    }
                  : undefined;
                queueResponseCreateAfterSessionUpdate(epoch, result.context, guard);
              } else {
                sendResponseCreate(epoch);
              }
            };
            if (didCancelForNewIntent) {
              // We just cancelled an in-flight answer for this new turn. Do NOT fire the
              // new response.create now: the realtime server won't run two responses at
              // once and would drop this one (the freeze). Defer it until the cancelled
              // response settles (its `response.done`), with a timeout as the safety net.
              clearPendingResponseAfterCancel();
              pendingResponseAfterCancelRef.current = { epoch, run: fireResponseCreate };
              pendingResponseAfterCancelTimerRef.current = setTimeout(() => {
                pendingResponseAfterCancelTimerRef.current = null;
                flushPendingResponseAfterCancel(epoch);
              }, RESPONSE_AFTER_CANCEL_SETTLE_MS);
            } else {
              fireResponseCreate();
            }
          }
          setStatus('thinking');
          pendingListenAfterPlaybackRef.current = false;
          stopCapture();
          break;
        }
        // OA2 — the GA upstream emits response.output_audio_transcript.*;
        // the legacy names stay accepted for recorded fixtures.
        case 'response.audio_transcript.delta':
        case 'response.output_audio_transcript.delta':
          if (isDiscardedResponseEvent(data)) break;
          activeResponseIdRef.current = responseIdFromEvent(data) ?? activeResponseIdRef.current;
          responseInProgressRef.current = true;
          if (data.delta) {
            recordFirstResponse(epoch, responseIdFromEvent(data));
            const guard = activeResponseGuardRef.current;
            if (guard) {
              const wasReleased = guard.released;
              guard.transcript += data.delta;
              if (!wasReleased) {
                const decision = classifyGuardedTranscript(guard, guard.transcript);
                if (decision === 'forbidden') {
                  failGuardedResponse(epoch, true);
                  break;
                }
                if (decision === 'safe') {
                  releaseGuardedResponse(epoch);
                }
              } else if (classifyGuardedTranscript(guard, guard.transcript) === 'forbidden') {
                recordQwenVoiceTrace({
                  source: 'frontend-hook',
                  type: 'audio_guard_post_release_anomaly',
                  responseId: guard.responseId ?? undefined,
                  textSnippet: guard.transcript,
                  contextSummary: summarizeQwenVoiceContext(guard.context),
                  details: {
                    kind: guard.kind,
                    retryCount: guard.retryCount,
                  },
                });
              }
              if (!guard.released) {
                setStatus('thinking');
                break;
              }
              if (wasReleased) {
                optionsRef.current.onAssistantTranscriptDelta(data.delta);
              }
            } else {
              optionsRef.current.onAssistantTranscriptDelta(data.delta);
            }
          }
          setStatus('speaking');
          break;
        case 'response.audio_transcript.done':
        case 'response.output_audio_transcript.done': {
          if (isDiscardedResponseEvent(data)) break;
          const finalText = (data.transcript ?? data.text ?? '').trim();
          const guard = activeResponseGuardRef.current;
          if (guard) {
            if (finalText) guard.transcript = finalText;
            if (!guard.released) {
              if (classifyGuardedTranscript(guard, guard.transcript || finalText) === 'forbidden') {
                failGuardedResponse(epoch, true);
                break;
              }
              releaseGuardedResponse(epoch);
            } else if (classifyGuardedTranscript(guard, guard.transcript || finalText) === 'forbidden') {
              recordQwenVoiceTrace({
                source: 'frontend-hook',
                type: 'audio_guard_post_release_anomaly',
                responseId: guard.responseId ?? undefined,
                textSnippet: guard.transcript || finalText,
                contextSummary: summarizeQwenVoiceContext(guard.context),
                details: {
                  kind: guard.kind,
                  retryCount: guard.retryCount,
                  phase: 'transcript_done',
                },
              });
            }
          }
          if (finalText) {
            recordConversationTurn('prof', finalText);
            optionsRef.current.onAssistantTranscriptFinal(finalText);
          }
          break;
        }
        case 'response.audio.delta':
          if (isDiscardedResponseEvent(data)) break;
          if (data.delta) {
            recordFirstResponse(epoch, responseIdFromEvent(data));
            activeResponseIdRef.current = responseIdFromEvent(data) ?? activeResponseIdRef.current;
            responseInProgressRef.current = true;
            const guard = activeResponseGuardRef.current;
            if (guard && !guard.released) {
              guard.audioQueue.push(data.delta);
              setStatus('thinking');
              break;
            }
            setStatus('speaking');
            if (!streamRef.current) void startCapture(epoch);
            playPcmChunk(data.delta, epoch);
          }
          break;
        case 'response.done': {
          const responseId = responseIdFromEvent(data);
          const discarded = responseId
            ? discardedResponseIdsRef.current.delete(responseId)
            : discardUnidentifiedResponseRef.current;
          if (!responseId && discarded) discardUnidentifiedResponseRef.current = false;
          if (discarded) {
            responseInProgressRef.current = false;
            activeResponseIdRef.current = null;
            latestTurnProofRef.current = null;
            suppressNextCancelledDoneRef.current = false;
            if (!isCurrentEpoch(epoch)) break;
            if (pendingResponseAfterCancelRef.current) {
              // The cancelled (barge-in) response has settled — fire the new turn's
              // deferred response now, instead of dropping it and going idle (the freeze).
              flushPendingResponseAfterCancel(epoch);
              break;
            }
            if (localPlaybackPendingRef.current) {
              setStatus('speaking');
            } else {
              setStatus('listening');
              if (!listeningRef.current) void startCapture(epoch);
            }
            break;
          }
          const cancelled = isCancelledResponse(data);
          recordQwenVoiceLifecycle('completion', { cancelled });
          const providerResponseId = responseId ?? undefined;
          const proof = latestTurnProofRef.current;
          const guard = activeResponseGuardRef.current;
          if (guard && !cancelled) {
            if (!guard.released) {
              if (classifyGuardedTranscript(guard, guard.transcript) === 'forbidden') {
                failGuardedResponse(epoch, true);
                break;
              }
              if (!guard.transcript.trim()) {
                if (failEmptyGuardedResponse(epoch, guard)) break;
              }
              const decision = classifyGuardedTranscript(guard, guard.transcript);
              if (decision !== 'safe') {
                failGuardedResponse(epoch, true);
                break;
              }
              releaseGuardedResponse(epoch);
            } else if (classifyGuardedTranscript(guard, guard.transcript) === 'forbidden') {
              recordQwenVoiceTrace({
                source: 'frontend-hook',
                type: 'audio_guard_post_release_anomaly',
                responseId: guard.responseId ?? undefined,
                textSnippet: guard.transcript,
                contextSummary: summarizeQwenVoiceContext(guard.context),
                details: {
                  kind: guard.kind,
                  retryCount: guard.retryCount,
                  phase: 'response_done',
                },
              });
            }
          }
          activeResponseGuardRef.current = null;
          responseInProgressRef.current = false;
          activeResponseIdRef.current = null;
          latestTurnProofRef.current = null;
          lastAcceptedUserTranscriptRef.current = null;
          if (cancelled && suppressNextCancelledDoneRef.current) {
            suppressNextCancelledDoneRef.current = false;
          } else {
            suppressNextCancelledDoneRef.current = false;
            optionsRef.current.onAssistantDone({
              providerResponseId,
              ...(!cancelled && proof && (proof.providerResponseId ?? null) === (providerResponseId ?? null) ? {
                turnClientId: proof.turnClientId,
                ...(proof.providerUsage ? { providerUsage: proof.providerUsage } : {}),
                ...(proof.providerUsageHash ? { providerUsageHash: proof.providerUsageHash } : {}),
                turnProof: proof.turnProof,
              } : {}),
              cancelled,
            });
          }
          if (!isCurrentEpoch(epoch)) break;
          if (cancelled && pendingResponseAfterCancelRef.current) {
            // The cancelled (barge-in) response has settled — now fire the new turn's
            // response that we deferred, instead of dropping it and going idle (the freeze).
            flushPendingResponseAfterCancel(epoch);
            break;
          }
          if (scheduledSourcesRef.current.length > 0) {
            pendingListenAfterPlaybackRef.current = true;
            setStatus('speaking');
          } else {
            setStatus('listening');
            if (!listeningRef.current) void startCapture(epoch);
          }
          break;
        }
        case 'error': {
          if (isDiscardedResponseEvent(data)) {
            recordQwenVoiceTrace({
              source: 'frontend-hook',
              type: 'stale_provider_error_ignored',
              responseId: responseIdFromEvent(data) ?? undefined,
              textSnippet: data.error?.message,
            });
            break;
          }
          const guard = activeResponseGuardRef.current;
          if (guard && !guard.released) {
            failGuardedResponse(epoch, false);
            break;
          }
          responseInProgressRef.current = false;
          activeResponseIdRef.current = null;
          failSession(epoch, data.error?.message ?? 'Errore voce AI.');
          break;
        }
      }
    };
  }, [
    cancelAssistantResponse,
    cancelUnresolvedAssistantForNewUserIntent,
    clearPendingResponseAfterCancel,
    clearPremint,
    clearReadinessTimer,
    clearReconnectTimer,
    clearResponseTracking,
    closeAudioContext,
    failEmptyGuardedResponse,
    failGuardedResponse,
    failSession,
    flushPendingResponseAfterCancel,
    hasUnresolvedAssistantTransaction,
    isCurrentEpoch,
    isDiscardedResponseEvent,
    playPcmChunk,
    queueResponseCreateAfterSessionUpdate,
    recordConversationTurn,
    recordFirstResponse,
    releaseGuardedResponse,
    resumeAfterLocalPlayback,
    schedulePremint,
    send,
    sendResponseCreate,
    sendUserTextItem,
    speakReconnectCopyOnce,
    startCapture,
    stopCapture,
    stopPlayback,
    supported,
    withConversationMemory,
  ]);

  useEffect(() => {
    startRef.current = start;
  }, [start]);

  useEffect(() => stop, [stop]);

  return {
    supported,
    active,
    listening,
    speaking,
    status,
    reconnecting,
    error,
    start,
    submitText,
    stop,
    interrupt,
  };
}
